/**
 * TransformGizmoInteraction — drop-in gizmo hosting for tools.
 *
 * Owns the default mouse choreography around a TransformGizmo so a tool
 * wires the full transform UX in a handful of delegating calls (the same
 * two-layer shape as OpeningFlipGizmos + OpeningFlipGizmoInteraction):
 *  - per-frame binding resolution + gizmo refresh (viewer `update` event),
 *  - `viewer.gizmoScene` assignment while attached,
 *  - arming on mousedown with orbit-control blocking + selection
 *    suppression (each opt-out-able via deps),
 *  - begin/commit/cancel lifecycle on the binding, undo-snapshot pushing,
 *  - trailing-click swallow after a moved drag and sub-threshold click
 *    release.
 *
 * Call the `handle*` methods FIRST in the tool's own handlers — a `true`
 * return means the gizmo consumed the interaction and the tool's own logic
 * must not run.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { DragEventData } from '../../../BaseTool';
import { ToolUndoRedoManager } from '../../ToolUndoRedoManager';
import { TransformGizmo, viewerGizmoHost } from './TransformGizmo';
import type { TransformGizmoBinding } from './TransformGizmoBinding';

export interface TransformGizmoInteractionDeps {
  viewer: Viewer;
  /** Live undo manager of the owning tool. */
  undoRedo: () => ToolUndoRedoManager | null;
  /**
   * Per-frame: the binding the gizmo should show, or null to hide. Return a
   * STABLE instance per target (rebind by mutating the binding's target);
   * changes are ignored while a drag is in flight.
   */
  resolveBinding: () => TransformGizmoBinding | null;
  /** Assign viewer.gizmoScene while attached (default true). */
  manageGizmoScene?: boolean;
  /** Suppress inputHandler.selectionDisabled during handle interactions —
   *  gizmo handles sit over empty space, so without this the parallel
   *  click-pick would deselect (default true). */
  manageSelectionSuppression?: boolean;
  /** blockOrbitControls while a handle is armed/dragged (default true). */
  manageOrbitControls?: boolean;
  /** Swallow the trailing click after a moved drag (default true). */
  swallowTrailingClick?: boolean;
  /** After a committed drag (the undo snapshot is already pushed). */
  onDragCommitted?: (binding: TransformGizmoBinding, handleName: string) => void;
}

export class TransformGizmoInteraction {
  /** Exposed for advanced configuration (setMode etc.). */
  readonly gizmo: TransformGizmo;

  private attached = false;
  /** Handle armed on mousedown, held until the drag starts or the click resolves. */
  private pendingHandle: string | null = null;
  private dragActive = false;
  /** True between a committed drag-end and its swallowed trailing click. */
  private justCommitted = false;
  /** Input locks (orbit block / selection suppression) currently held. */
  private locksHeld = false;
  private readonly boundRefresh = (): void => this.refresh();

  constructor(private readonly deps: TransformGizmoInteractionDeps) {
    this.gizmo = new TransformGizmo(viewerGizmoHost(deps.viewer));
  }

  /** Start hosting (call from the tool's onEnable). */
  attach(): void {
    if (this.attached) return;
    this.attached = true;
    if (this.deps.manageGizmoScene ?? true) this.deps.viewer.gizmoScene = this.gizmo.scene;
    this.deps.viewer.addEventListener('update', this.boundRefresh);
  }

  /** Tear down hosting (call from the tool's onDisable). Safe mid-drag. */
  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    this.deps.viewer.removeEventListener('update', this.boundRefresh);
    if (this.dragActive) {
      this.gizmo.endDrag();
      this.gizmo.getBinding()?.cancelDrag();
    }
    this.pendingHandle = null;
    this.dragActive = false;
    this.releaseInputLocks();
    this.gizmo.setBinding(null);
    if ((this.deps.manageGizmoScene ?? true) && this.deps.viewer.gizmoScene === this.gizmo.scene) {
      this.deps.viewer.gizmoScene = null;
    }
  }

  dispose(): void {
    this.detach();
    this.gizmo.dispose();
  }

  /** True when the cursor sits on a visible gizmo handle (cursor feedback). */
  isOverHandle(event: MouseEvent): boolean {
    return this.gizmo.getBinding() !== null && this.gizmo.updateActiveHandle(this.pixelMouse(event)) !== null;
  }

  /** Hover highlight; true while over a handle (tool shows a grab cursor and
   *  skips its own hover logic). */
  handleMouseMove(event: MouseEvent): boolean {
    if (this.dragActive) return true;
    if (!this.gizmo.getBinding()) return false;
    return this.gizmo.updateActiveHandle(this.pixelMouse(event)) !== null;
  }

  /** Arm a handle drag. True = consumed. */
  handleMouseDown(event: MouseEvent): boolean {
    if (event.button !== 0) return false;
    if (!this.gizmo.getBinding()) return false;
    const handle = this.gizmo.updateActiveHandle(this.pixelMouse(event));
    if (!handle) return false;
    this.pendingHandle = handle;
    this.acquireInputLocks();
    return true;
  }

  /** Begin the armed drag. True = consumed. */
  handleDragStart(data: DragEventData): boolean {
    if (!this.pendingHandle || data.button !== 0) return false;
    const px = this.pixelMouse(data.event);
    const worldPoint = this.gizmo.getHandleIntersection(this.pendingHandle, px);
    if (worldPoint && this.gizmo.startDrag(this.pendingHandle, px, worldPoint)) {
      this.dragActive = true;
    } else {
      // Couldn't anchor the drag — release, but still consume the gesture
      // (it started on a handle; the tool must not box-select from it).
      this.pendingHandle = null;
      this.releaseInputLocks();
    }
    return true;
  }

  handleDrag(data: DragEventData): boolean {
    if (!this.dragActive) return false;
    // Shift = raw transform (bindings skip collision/snap assists).
    this.gizmo.processDrag(this.pixelMouse(data.event), { override: data.event.shiftKey });
    return true;
  }

  /** Commit or cancel the drag; pushes the binding's undo snapshot. */
  handleDragEnd(_data: DragEventData): boolean {
    if (!this.dragActive) {
      if (!this.pendingHandle) return false;
      // Armed but the drag never started (anchor failed) — just release.
      this.pendingHandle = null;
      this.releaseInputLocks();
      return true;
    }
    const handleName = this.pendingHandle ?? '';
    const binding = this.gizmo.getBinding();
    const moved = this.gizmo.endDrag();
    this.dragActive = false;
    this.pendingHandle = null;
    this.releaseInputLocks();
    if (binding) {
      if (moved) {
        const snapshot = binding.commitDrag();
        if (snapshot) this.deps.undoRedo()?.pushState(snapshot);
        this.deps.onDragCommitted?.(binding, handleName);
        if (this.deps.swallowTrailingClick ?? true) {
          // Keep the swallow flag until the trailing click resolved (same
          // next-tick pattern the tools use for their own drags).
          this.justCommitted = true;
          setTimeout(() => {
            this.justCommitted = false;
          }, 0);
        }
      } else {
        binding.cancelDrag();
      }
    }
    return true;
  }

  /** Sub-threshold handle click / trailing click of a moved drag. True = consumed. */
  handleClick(_event: MouseEvent): boolean {
    if (this.pendingHandle) {
      // Mousedown hit a handle but no drag followed — release the arm and
      // swallow the click (a handle click is not a canvas click).
      this.pendingHandle = null;
      this.releaseInputLocks();
      return true;
    }
    return this.justCommitted;
  }

  // ─── Internals ──────────────────────────────────────────────────

  private refresh(): void {
    // Never rebind mid-interaction — the drag's baseline lives on the binding.
    if (!this.dragActive && !this.pendingHandle) {
      this.gizmo.setBinding(this.deps.resolveBinding());
    }
    this.gizmo.update(this.deps.viewer.scene.getActiveCamera(), this.deps.viewer.renderer.domElement);
  }

  private pixelMouse(event: MouseEvent): THREE.Vector2 {
    const rect = this.deps.viewer.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  private acquireInputLocks(): void {
    if (this.locksHeld) return;
    this.locksHeld = true;
    if (this.deps.manageOrbitControls ?? true) this.deps.viewer.inputHandler.blockOrbitControls();
    if (this.deps.manageSelectionSuppression ?? true) this.deps.viewer.inputHandler.selectionDisabled = true;
  }

  private releaseInputLocks(): void {
    if (!this.locksHeld) return;
    this.locksHeld = false;
    if (this.deps.manageOrbitControls ?? true) this.deps.viewer.inputHandler.unblockOrbitControls();
    if (this.deps.manageSelectionSuppression ?? true) this.deps.viewer.inputHandler.selectionDisabled = false;
  }
}
